import React, { useEffect, useState, useCallback } from 'react';
import Navbar from '@/components/Navbar';
import { documentsAPI, serviceRequestsAPI } from '@/lib/api';
import { Folder, FileText, Download, Calendar, Lock, Upload, CheckCircle, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const TaxLockerPage = () => {
  const [documents, setDocuments] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadServiceRequest, setUploadServiceRequest] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [docType, setDocType] = useState('');
  const [docPassword, setDocPassword] = useState('');
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordDocId, setPasswordDocId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [docsRes, requestsRes] = await Promise.all([
        documentsAPI.getAll(),
        serviceRequestsAPI.getAll()
      ]);
      
      setDocuments(docsRes.data);
      setServiceRequests(requestsRes.data);
    } catch (err) {
      console.error('Error loading tax locker data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUploadClick = (serviceRequestId) => {
    setUploadServiceRequest(serviceRequestId);
    setShowUploadModal(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile || !docType) {
      alert('Please select a file and enter document type');
      return;
    }

    // Check file size based on file type (ZIP files get 25MB, others 5MB)
    const fileSizeMB = uploadFile.size / (1024 * 1024);
    const isZipFile = uploadFile.name.toLowerCase().endsWith('.zip');
    const MAX_FILE_SIZE_MB = isZipFile ? 25 : 5;
    
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      alert(`File size (${fileSizeMB.toFixed(2)}MB) exceeds the maximum allowed size of ${MAX_FILE_SIZE_MB}MB${isZipFile ? ' for ZIP files' : ''}. Please compress or reduce the file size before uploading.`);
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result;
        
        await documentsAPI.upload({
          service_request_id: uploadServiceRequest,
          doc_type: docType,
          file_data: base64Data,
          password: docPassword || undefined,
          original_filename: uploadFile.name
        });

        // Reload data
        await loadData();
        
        // Reset and close modal
        setShowUploadModal(false);
        setUploadFile(null);
        setDocType('');
        setDocPassword('');
        setUploadServiceRequest(null);
      };
      reader.readAsDataURL(uploadFile);
    } catch (err) {
      console.error('Error uploading document:', err);
      alert('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveDocument = (docId) => {
    setDocToDelete(docId);
    setShowDeleteDialog(true);
  };

  const confirmDeleteDocument = async () => {
    if (!docToDelete) return;

    setRemoving(docToDelete);
    try {
      await documentsAPI.delete(docToDelete);
      setDocuments(prev => prev.filter(d => d.id !== docToDelete));
      setSelectedDocs(prev => prev.filter(id => id !== docToDelete));
    } catch (err) {
      console.error('Error removing document:', err);
      alert(err.response?.data?.detail || 'Failed to remove document. Please try again.');
    } finally {
      setRemoving(null);
      setShowDeleteDialog(false);
      setDocToDelete(null);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedDocs([]);
      setSelectAll(false);
    } else {
      // Only select documents from unpaid service requests
      const deletableDocs = filteredDocs.filter(doc => {
        const request = serviceRequests.find(r => r.id === doc.service_request_id);
        return request && request.payment_status !== 'paid';
      }).map(doc => doc.id);
      setSelectedDocs(deletableDocs);
      setSelectAll(true);
    }
  };

  const handleCheckboxChange = (docId) => {
    if (selectedDocs.includes(docId)) {
      setSelectedDocs(prev => prev.filter(id => id !== docId));
    } else {
      setSelectedDocs(prev => [...prev, docId]);
    }
  };

  const handleRemoveSelected = async () => {
    if (selectedDocs.length === 0) {
      alert('Please select at least one document to remove');
      return;
    }
    setBulkDeleteDialog(true);
  };

  const confirmBulkDelete = async () => {
    try {
      setRemoving('batch');
      
      // Delete each selected document
      const deletePromises = selectedDocs.map(id => documentsAPI.delete(id));
      await Promise.all(deletePromises);
      
      setDocuments(prev => prev.filter(doc => !selectedDocs.includes(doc.id)));
      setSelectedDocs([]);
      setSelectAll(false);
    } catch (err) {
      console.error('Error removing documents:', err);
      alert('Failed to remove some documents. Please try again.');
    } finally {
      setRemoving(null);
      setBulkDeleteDialog(false);
    }
  };

  const filteredDocs = selectedRequest === 'all' 
    ? documents 
    : documents.filter(doc => doc.service_request_id === selectedRequest);

  const groupedDocs = filteredDocs.reduce((acc, doc) => {
    const requestId = doc.service_request_id;
    if (!acc[requestId]) {
      acc[requestId] = [];
    }
    acc[requestId].push(doc);
    return acc;
  }, {});

  // Check if a document can be deleted (service not paid)
  const canDeleteDoc = (docId) => {
    const doc = documents.find(d => d.id === docId);
    if (!doc) return false;
    const request = serviceRequests.find(r => r.id === doc.service_request_id);
    return request && request.payment_status !== 'paid';
  };

  const handleSetPassword = (docId) => {
    const doc = documents.find(d => d.id === docId);
    if (doc) {
      setPasswordDocId(docId);
      setNewPassword(doc.password || '');
      setShowPasswordModal(true);
    }
  };

  const handleUpdatePassword = async () => {
    if (!passwordDocId) return;

    setUpdatingPassword(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${passwordDocId}/password`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ password: newPassword })
      });

      if (response.ok) {
        // Update local state
        setDocuments(prev => prev.map(doc => 
          doc.id === passwordDocId 
            ? { ...doc, password: newPassword || null }
            : doc
        ));
        alert('Password updated successfully!');
        setShowPasswordModal(false);
        setPasswordDocId(null);
        setNewPassword('');
      } else {
        const error = await response.json();
        alert(`Failed to update password: ${error.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error updating password:', err);
      alert('Failed to update password. Please try again.');
    } finally {
      setUpdatingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="upload-modal">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-2xl font-bold mb-6">Upload Document</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Document Type *</label>
                <input
                  type="text"
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  placeholder="e.g., PAN Card, Aadhaar, Form 16"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="doc-type-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select File *</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.zip"
                  onChange={handleFileSelect}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="file-input"
                />
                {uploadFile && (
                  <p className="text-sm text-green-600 mt-2">Selected: {uploadFile.name}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password (Optional)</label>
                <input
                  type="password"
                  value={docPassword}
                  onChange={(e) => setDocPassword(e.target.value)}
                  placeholder="If document is password protected"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="doc-password-input"
                />
                <p className="text-xs text-gray-500 mt-1">Enter password if your document is protected</p>
              </div>
              
              <p className="text-xs text-gray-500">Accepted formats: PDF, JPG, PNG, ZIP (Max 5MB for images/PDFs, 25MB for ZIP files)</p>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleUploadDocument}
                disabled={uploading}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="upload-submit-button"
              >
                {uploading ? (
                  'Uploading...'
                ) : (
                  <>
                    <Upload size={20} />
                    Upload
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                  setDocType('');
                  setDocPassword('');
                  setUploadServiceRequest(null);
                }}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
                data-testid="upload-cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-2 flex items-center gap-3" data-testid="tax-locker-title">
              <Lock className="text-blue-600" size={40} />
              Tax Locker
            </h1>
            <p className="text-gray-600">Your documents are stored securely and accessible anytime</p>
          </div>
          <button
            onClick={() => {
              if (serviceRequests.length === 0) {
                alert('No service requests found. Please select a service first.');
                return;
              }
              handleUploadClick(serviceRequests[0].id);
            }}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center gap-2"
            data-testid="upload-document-button"
          >
            <Upload size={20} />
            Upload Documents
          </button>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="locker-stat-documents">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Total Documents</p>
                <p className="text-3xl font-bold text-gray-900">{documents.length}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <FileText className="text-blue-600" size={28} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="locker-stat-services">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Service Requests</p>
                <p className="text-3xl font-bold text-gray-900">{Object.keys(groupedDocs).length}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <Folder className="text-green-600" size={28} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="locker-stat-storage">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm mb-1">Storage Used</p>
                <p className="text-3xl font-bold text-gray-900">Secure</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-lg">
                <Lock className="text-purple-600" size={28} />
              </div>
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8" data-testid="locker-filter">
          <label className="block text-sm font-medium text-gray-700 mb-3">Filter by Service</label>
          <select
            value={selectedRequest}
            onChange={(e) => setSelectedRequest(e.target.value)}
            className="w-full md:w-96 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="service-filter-select"
          >
            <option value="all">All Services</option>
            {serviceRequests.map(req => (
              <option key={req.id} value={req.id}>
                {req.service_name} - {new Date(req.created_at).toLocaleDateString()}
              </option>
            ))}
          </select>
        </div>

        {/* Documents */}
        {documents.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center" data-testid="no-documents">
            <Folder className="text-gray-400 mx-auto mb-4" size={64} />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Documents Yet</h3>
            <p className="text-gray-600 mb-6">Your uploaded documents will appear here</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedDocs).map(([requestId, docs]) => {
              const request = serviceRequests.find(r => r.id === requestId);
              const isPaid = request && request.payment_status === 'paid';
              
              return (
                <div key={requestId} className="bg-white rounded-xl shadow-sm p-6" data-testid={`document-group-${requestId}`}>
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                    <div>
                      <h3 className="text-xl font-semibold">{request?.service_name}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        <Calendar className="inline mr-1" size={14} />
                        {new Date(request?.created_at).toLocaleDateString('en-IN', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                      {isPaid && (
                        <p className="text-xs text-green-600 mt-1 font-medium">
                          🔒 Paid Service - Documents protected
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-sm bg-blue-50 text-blue-700 px-4 py-2 rounded-lg font-medium">
                        {docs.length} document{docs.length !== 1 ? 's' : ''}
                      </div>
                      {!isPaid && docs.length > 0 && (
                        <>
                          <label className="flex items-center gap-2 cursor-pointer text-sm font-medium" data-testid={`select-all-${requestId}`}>
                            <input
                              type="checkbox"
                              checked={docs.every(d => selectedDocs.includes(d.id))}
                              onChange={() => {
                                const allSelected = docs.every(d => selectedDocs.includes(d.id));
                                if (allSelected) {
                                  setSelectedDocs(prev => prev.filter(id => !docs.map(d => d.id).includes(id)));
                                } else {
                                  setSelectedDocs(prev => [...prev, ...docs.map(d => d.id).filter(id => !prev.includes(id))]);
                                }
                              }}
                              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            Select All
                          </label>
                          {selectedDocs.some(id => docs.map(d => d.id).includes(id)) && (
                            <button
                              onClick={handleRemoveSelected}
                              disabled={removing === 'batch'}
                              className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-700 transition disabled:opacity-50"
                              data-testid={`remove-selected-${requestId}`}
                            >
                              {removing === 'batch' ? 'Removing...' : `Remove Selected (${selectedDocs.filter(id => docs.map(d => d.id).includes(id)).length})`}
                            </button>
                          )}
                        </>
                      )}
                      <button
                        onClick={() => handleUploadClick(requestId)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition flex items-center gap-2"
                        data-testid={`upload-for-${requestId}`}
                      >
                        <Upload size={16} />
                        Upload
                      </button>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {docs.map((doc) => (
                      <div 
                        key={doc.id} 
                        className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition"
                        data-testid={`document-${doc.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {!isPaid && (
                              <input
                                type="checkbox"
                                checked={selectedDocs.includes(doc.id)}
                                onChange={() => handleCheckboxChange(doc.id)}
                                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                data-testid={`checkbox-${doc.id}`}
                              />
                            )}
                            <div className="bg-blue-100 p-2 rounded-lg">
                              <FileText className="text-blue-600" size={24} />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const headers = {};
                                  
                                  if (token) {
                                    headers['Authorization'] = `Bearer ${token}`;
                                  }
                                  
                                  const response = await fetch(
                                    `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`,
                                    { 
                                      headers
                                    }
                                  );
                                  
                                  if (!response.ok) {
                                    throw new Error('Download failed');
                                  }
                                  
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  
                                  // Extract filename from file_path or use doc_type
                                  const filename = doc.file_path ? 
                                    doc.file_path.split('/').pop() : 
                                    `${doc.doc_type.replace(/\s+/g, '_')}.pdf`;
                                  
                                  link.download = filename;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(url);
                                } catch (error) {
                                  console.error('Download error:', error);
                                  alert('Failed to download file. Please try again or contact support.');
                                }
                              }}
                              className="text-green-600 hover:text-green-700 p-1 hover:bg-green-50 rounded inline-flex items-center justify-center" 
                              title="Download"
                              data-testid={`download-${doc.id}`}
                            >
                              <Download size={20} />
                            </button>
                            {!isPaid && (
                              <button 
                                onClick={() => handleRemoveDocument(doc.id)}
                                disabled={removing === doc.id}
                                className="text-red-600 hover:text-red-700 p-1 hover:bg-red-50 rounded disabled:opacity-50" 
                                title="Remove"
                                data-testid={`remove-${doc.id}`}
                              >
                                <Trash2 size={20} />
                              </button>
                            )}
                          </div>
                        </div>
                        <h4 className="font-medium mb-1">{doc.doc_type}</h4>
                        <p className="text-xs text-gray-500 mb-2">
                          Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                        </p>
                        {doc.password ? (
                          <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2">
                            <p className="text-xs text-amber-800 font-medium mb-1">🔒 Protected</p>
                            <div className="flex items-center justify-between gap-1">
                              <div className="flex items-center gap-1">
                                <p className="text-xs text-amber-700">Password:</p>
                                <code className="text-xs font-mono bg-amber-100 px-2 py-0.5 rounded text-amber-900">
                                  {doc.password}
                                </code>
                              </div>
                              <button
                                onClick={() => handleSetPassword(doc.id)}
                                className="text-xs text-amber-700 hover:text-amber-900 underline"
                                title="Update password"
                              >
                                Edit
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleSetPassword(doc.id)}
                            className="mt-2 w-full text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg flex items-center justify-center gap-2 transition"
                          >
                            <Lock size={14} />
                            Set Password
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Document?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this document? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteDocument} className="bg-red-600 hover:bg-red-700">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialog} onOpenChange={setBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {selectedDocs.length} Document(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {selectedDocs.length} selected document(s)? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBulkDelete} className="bg-red-600 hover:bg-red-700">
              Remove All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-4">Set Document Password</h2>
            <p className="text-gray-600 mb-4 text-sm">
              Add or update password protection for this document
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter password (leave empty to remove)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This password will be displayed to you. Remember to share it securely if needed.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleUpdatePassword}
                disabled={updatingPassword}
                className="flex-1 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
              >
                {updatingPassword ? 'Updating...' : 'Update Password'}
              </button>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordDocId(null);
                  setNewPassword('');
                }}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxLockerPage;
